import { useLazyQuery, useMutation } from '@apollo/client';
import { MUTATION_UPDATE_USER_PREFERENCES } from 'graphql/mutations/propertiesPanel';
import { GET_USER_PREFERENCES } from 'graphql/queries/propertiesPanel';
import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { FieldsOrder, UserPreferencesComponentKey } from 'ui-component/RecordView/PropertiesPanel/utils';

// ==============================|| PANEL FORMS CONTEXT & PROVIDER ||============================== //

interface UserPreferencesData {
    componentKey: UserPreferencesComponentKey;
    recordTypeId: number;
    preferenceData: any;
    name: string;
    isDefault: boolean;
    userId: string;
    tenantId: number;
}

// Define the context interface
interface PanelFormsContextType {
    // Initial field order functionality
    initialFieldOrder: string[];
    setInitialFieldOrder: (value: string[]) => void;

    initialObjectFieldsOrder: string[];
    setInitialObjectFieldsOrder: (value: string[]) => void;

    // Search functionality
    searchInput: string;
    setSearchInput: (value: string) => void;

    // Sort functionality
    sort: 'none' | 'asc' | 'desc';
    setSort: (value: 'none' | 'asc' | 'desc') => void;

    // Drag and drop functionality
    enableDragAndDrop: boolean;
    setEnableDragAndDrop: (value: boolean) => void;

    // Fields order functionality
    fieldsOrder: FieldsOrder;
    setFieldsOrder: (value: FieldsOrder) => void;

    // Custom field order functionality
    customFieldOrder: string[];
    setCustomFieldOrder: (value: string[]) => void;

    // Pinned fields functionality
    pinnedFields: string[];
    setPinnedFields: (value: string[]) => void;

    // Custom object fields order functionality
    customObjectFieldsOrder: string[];
    setCustomObjectFieldsOrder: (value: string[]) => void;

    // User preferences functionality
    userPreferences: any;
    getUserPreferences: (recordTypeId: number, isCreate?: boolean) => void;
    createOrUpdateUserPreferences: (variables: any) => Promise<any>;
    updateLoading: boolean;
    loading: boolean;

    // Create mode flag
    setIsCreate: (value: boolean) => void;
}

// Initial state
const initialState: PanelFormsContextType = {
    initialFieldOrder: [],
    setInitialFieldOrder: () => {},
    initialObjectFieldsOrder: [],
    setInitialObjectFieldsOrder: () => {},
    searchInput: '',
    setSearchInput: () => {},
    sort: 'none',
    setSort: () => {},
    enableDragAndDrop: false,
    setEnableDragAndDrop: () => {},
    fieldsOrder: FieldsOrder.Default,
    setFieldsOrder: () => {},
    customFieldOrder: [],
    setCustomFieldOrder: () => {},
    pinnedFields: [],
    setPinnedFields: () => {},
    customObjectFieldsOrder: [],
    setCustomObjectFieldsOrder: () => {},
    userPreferences: {},
    getUserPreferences: () => {},
    createOrUpdateUserPreferences: async () => {},
    updateLoading: false,
    loading: true,
    setIsCreate: () => {}
};

const PanelFormsContext = createContext<PanelFormsContextType>(initialState);

type PanelFormsProviderProps = {
    children: ReactNode;
};

interface DefaultValues {
    preferenceData: {
        sort: 'none' | 'asc' | 'desc';
        fieldsOrder: FieldsOrder;
        customFieldOrder: string[];
        customObjectFieldsOrder: string[];
        pinnedFields: string[];
    };
    id: number;
    name: string;
    isDefault: boolean;
}

const PanelFormsProvider = ({ children }: PanelFormsProviderProps) => {
    const defaultValues: DefaultValues = {
        preferenceData: {
            sort: 'none',
            fieldsOrder: FieldsOrder.Default,
            pinnedFields: [],
            customFieldOrder: [],
            customObjectFieldsOrder: []
        },
        id: 1,
        name: 'Default Metafield Preference',
        isDefault: true
    };

    const dispatch = useDispatch();

    // Create mode flag
    const [isCreate, setIsCreate] = useState(false);

    // Search state
    const [searchInput, setSearchInput] = useState('');

    // Sort state
    const [sort, setSort] = useState<'none' | 'asc' | 'desc'>(defaultValues.preferenceData.sort);

    // Drag and drop state
    const [enableDragAndDrop, setEnableDragAndDrop] = useState(false);

    // Fields order state
    const [fieldsOrder, setFieldsOrder] = useState<FieldsOrder>(defaultValues.preferenceData.fieldsOrder);

    // Custom field order state
    const [customFieldOrder, setCustomFieldOrder] = useState<string[]>(defaultValues.preferenceData.customFieldOrder);

    // Custom object fields order state
    const [customObjectFieldsOrder, setCustomObjectFieldsOrder] = useState<string[]>(defaultValues.preferenceData.customObjectFieldsOrder);

    // Handle pinned fields state
    const [pinnedFields, setPinnedFields] = useState<string[]>(defaultValues.preferenceData.pinnedFields);

    // Initial field order state
    const [initialFieldOrder, setInitialFieldOrder] = useState<string[]>(defaultValues.preferenceData.customFieldOrder);

    const [initialObjectFieldsOrder, setInitialObjectFieldsOrder] = useState<string[]>(
        defaultValues.preferenceData.customObjectFieldsOrder
    );

    // User preferences state
    const [userPreferences, setUserPreferences] = useState<any>({});
    const [loading, setLoading] = useState(true);

    // Query to get user preferences
    const [getUserPreferencesQuery] = useLazyQuery(GET_USER_PREFERENCES, {
        onCompleted: (data) => {
            const preferences: UserPreferencesData[] = data.getUserPreferences;
            if (preferences.length) {
                updateStateContext(preferences);
            } else updateStateContext([defaultValues]);
            setLoading(false);
        },
        onError: (error) => {
            setLoading(false);
            updateStateContext({
                preferenceData: defaultValues
            });
        },
        fetchPolicy: 'network-only'
    });

    // Mutation for updating user preferences
    const [createOrUpdateUserPreferencesMutation, { loading: updateLoading, error: updateError }] = useMutation(
        MUTATION_UPDATE_USER_PREFERENCES,
        {
            onCompleted: (data) => {
                let preferences: UserPreferencesData[] = [];
                const existingPreferenceWasEdited = userPreferences?.find(
                    (preference: UserPreferencesData) => preference.name === data?.createOrUpdateUserPreferences?.name
                );
                if (existingPreferenceWasEdited) {
                    preferences = userPreferences?.map((preference: UserPreferencesData) =>
                        preference.name === data.createOrUpdateUserPreferences?.name
                            ? { ...data?.createOrUpdateUserPreferences }
                            : preference
                    );
                } else {
                    preferences = [...userPreferences, { ...data?.createOrUpdateUserPreferences }];
                }

                if (preferences) {
                    updateStateContext(preferences);
                    setEnableDragAndDrop(false);
                }

                dispatch(
                    openSnackbar({
                        open: true,
                        title: `Metafield Preference Created`,
                        variant: 'alert',
                        alert: { color: 'success' },
                        close: true
                    })
                );
            },
            onError: () => {
                setEnableDragAndDrop(false);
                dispatch(
                    openSnackbar({
                        open: true,
                        title: `Update failed ${updateError?.message}`,
                        variant: 'alert',
                        alert: { color: 'error' },
                        close: true
                    })
                );
            }
        }
    );

    // Function to get user preferences
    const getUserPreferences = useCallback(
        (recordTypeId: number) => {
            setLoading(true);
            getUserPreferencesQuery({
                variables: {
                    componentKey: UserPreferencesComponentKey.DetailsPanel,
                    recordTypeId
                }
            });
        },
        [getUserPreferencesQuery, setLoading]
    );

    const updateStateContext = (newPreferences: any) => {
        if (newPreferences?.length) {
            const {
                sort: newSort,
                fieldsOrder: newFieldsOrder,
                customFieldOrder: newCustomFieldOrder,
                customObjectFieldsOrder: newCustomObjectFieldsOrder,
                pinnedFields: newPinnedFields
            } = newPreferences?.find((preference: any) => preference.isDefault).preferenceData;

            setUserPreferences(newPreferences);

            if (newSort) setSort(newSort);
            if (newFieldsOrder) setFieldsOrder(newFieldsOrder);
            if (newCustomFieldOrder) setCustomFieldOrder(newCustomFieldOrder);
            if (newCustomObjectFieldsOrder) setCustomObjectFieldsOrder(newCustomObjectFieldsOrder);
            if (newPinnedFields) setPinnedFields(newPinnedFields);
        }
    };

    // Function to create or update user preferences
    const createOrUpdateUserPreferences = useCallback(
        async (data: UserPreferencesData) => {
            const newData = await createOrUpdateUserPreferencesMutation({
                variables: {
                    data
                }
            });
            return newData;
        },
        [createOrUpdateUserPreferencesMutation]
    );

    useEffect(() => {
        if (sort !== 'none') {
            setCustomFieldOrder([]);
        }
    }, [sort, setCustomFieldOrder]);

    useEffect(() => {
        if (isCreate) setLoading(false);
    }, [isCreate, setLoading]);

    return (
        <PanelFormsContext.Provider
            value={{
                searchInput,
                setSearchInput,
                sort,
                setSort,
                enableDragAndDrop,
                setEnableDragAndDrop,
                fieldsOrder,
                setFieldsOrder,
                customFieldOrder,
                setCustomFieldOrder,
                pinnedFields,
                setPinnedFields,
                customObjectFieldsOrder,
                setCustomObjectFieldsOrder,
                initialFieldOrder,
                setInitialFieldOrder,
                initialObjectFieldsOrder,
                setInitialObjectFieldsOrder,
                userPreferences,
                getUserPreferences,
                createOrUpdateUserPreferences,
                updateLoading,
                loading,
                setIsCreate
            }}
        >
            {children}
        </PanelFormsContext.Provider>
    );
};

export { PanelFormsContext, PanelFormsProvider };
